import axios from 'axios';
import { Storage } from 'react-jhipster';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { jwtDecode, JwtPayload } from 'jwt-decode';

const TIMEOUT = 1 * 60 * 1000;
axios.defaults.timeout = TIMEOUT;
axios.defaults.baseURL = SERVER_API_URL;

let isRefreshing = false;
let isLoggingOut = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

export const refreshToken = async () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(
      '/api/refresh-token',
      {
        refreshToken: localStorage.getItem('refreshToken') || Storage.session.get('refreshToken'),
        rememberMe: false,
      },
      {
        headers: { Authorization: undefined },
      }
    );

    const newToken = response.data?.accessToken;
    const newRefreshToken = response.data?.refreshToken;
    if (newToken) {
      Storage.local.set('jhi-authenticationToken', newToken);
      Storage.session.set('jhi-authenticationToken', newToken);
      Cookies.set('jhi-authenticationToken', newToken);

      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      processQueue(null, newToken);
      return newToken;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    console.error('Refresh token failed:', error);
    processQueue(error, null);

    localStorage.removeItem('jhi-authenticationToken');
    sessionStorage.removeItem('jhi-authenticationToken');
    Cookies.remove('jhi-authenticationToken');

    return null;
  } finally {
    isRefreshing = false;
  }
};

const setupAxiosInterceptors = onUnauthenticated => {
  const onRequestSuccess = async config => {
    let token =
      Storage.local.get('jhi-authenticationToken') ||
      Cookies.get('jhi-authenticationToken') ||
      Storage.session.get('jhi-authenticationToken');

    const isRefreshRequest = config.url?.includes('/api/refresh-token');

    if (token && !isRefreshRequest) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded.exp * 1000 < Date.now()) {
          const newToken = await refreshToken();
          if (newToken) token = newToken;
          else token = null;
        }
      } catch {}
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  };

  const onResponseSuccess = response => response;

  const onResponseError = async error => {
    const status = error.status || (error.response ? error.response.status : 0);
    const isRefreshRequest = error.config?.url?.includes('/api/refresh-token');

    if (status === 401 && !error.config._retry && !isRefreshRequest) {
      error.config._retry = true;
      console.log('Token expired — attempting refresh');

      localStorage.removeItem('jhi-authenticationToken');
      sessionStorage.removeItem('jhi-authenticationToken');
      Cookies.remove('jhi-authenticationToken');
      if (localStorage.getItem('refreshToken') || Storage.session.get('refreshToken')) {
        const newToken = await refreshToken();

        if (newToken) {
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return axios.request(error.config);
        } else {
          if (!isLoggingOut) {
            isLoggingOut = true;
            delete axios.defaults.headers.common['Authorization'];
            onUnauthenticated();
            toast.error('Session expired. Please log in again.');
            setTimeout(() => { isLoggingOut = false; }, 3000);
          }
        }
      } else {
        if (!isLoggingOut) {
          isLoggingOut = true;
          onUnauthenticated();
          toast.error('Your session has ended. Please log in again.');
          setTimeout(() => { isLoggingOut = false; }, 3000);
        }
      }
    }

    return Promise.reject(error);
  };

  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
